import { Controller } from "@hotwired/stimulus"

// Guest picker on the New Meeting form. A searchable checkbox list of
// project team members (each with a role badge). Tracks a live
// "N selected" count and supports search-filter + select-all / none.
// Posts `guest_user_ids[]`; meetings#create turns each into a
// MeetingAttendee.
//
// An optional "Include non-project members" toggle reveals a second,
// distinctly-badged group. When the toggle is off, that group is
// hidden AND its checkboxes are cleared so they never submit.
export default class extends Controller {
  static targets = [
    "search",
    "row",
    "checkbox",
    "count",
    "nonMemberGroup",
    "nonMemberToggle",
    "scope"
  ]

  connect() {
    // Per-row "This meeting / All meetings" pickers only make sense on
    // recurring meetings — track the recurrence select live, same as
    // the email-chips controller does.
    this._freqEl = document.querySelector('select[name="recurrence[frequency]"]')
    if (this._freqEl) {
      this._onFreq = () => this._syncScopeVisibility()
      this._freqEl.addEventListener("change", this._onFreq)
    }
    this.updateCount()
  }

  disconnect() {
    if (this._freqEl && this._onFreq) {
      this._freqEl.removeEventListener("change", this._onFreq)
    }
  }

  get isRecurring() {
    const v = this._freqEl ? this._freqEl.value : "none"
    return !!v && v !== "none"
  }

  // Show each row's scope select only while the meeting is recurring
  // AND that row is checked.
  _syncScopeVisibility() {
    const recurring = this.isRecurring
    this.rowTargets.forEach((row) => {
      const cb = row.querySelector('input[type="checkbox"]')
      const scope = row.querySelector('[data-meeting-guests-target="scope"]')
      if (scope) scope.classList.toggle("hidden", !(recurring && cb && cb.checked))
    })
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    this.rowTargets.forEach((row) => {
      const haystack = row.dataset.name || ""
      row.classList.toggle("hidden", q.length > 0 && !haystack.includes(q))
    })
  }

  updateCount() {
    this._syncScopeVisibility()
    if (!this.hasCountTarget) return
    const n = this.checkboxTargets.filter((c) => c.checked).length
    this.countTarget.textContent = n
  }

  // Reveal / hide the non-project-members group. Hiding also clears
  // any checked rows in it so they aren't submitted.
  toggleNonMembers() {
    if (!this.hasNonMemberGroupTarget) return
    const on = this.nonMemberToggleTarget.checked
    this.nonMemberGroupTarget.classList.toggle("hidden", !on)
    if (!on) {
      this.nonMemberGroupTarget
        .querySelectorAll('input[type="checkbox"]')
        .forEach((cb) => { cb.checked = false })
      this.updateCount()
    }
  }

  // Select all currently-visible rows (respects the active filter AND
  // a collapsed non-member group — offsetParent is null when any
  // ancestor is hidden).
  selectAll(event) {
    event.preventDefault()
    this.rowTargets.forEach((row) => {
      if (row.classList.contains("hidden") || row.offsetParent === null) return
      const cb = row.querySelector('input[type="checkbox"]')
      if (cb) cb.checked = true
    })
    this.updateCount()
  }

  clearAll(event) {
    event.preventDefault()
    // The organizer's row is disabled and always attends — "None" skips it.
    this.checkboxTargets.forEach((cb) => { if (!cb.disabled) cb.checked = false })
    this.updateCount()
  }
}
